import React, { useCallback, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import DashboardPOAssetTypesTable from '@components/routes/AdminPanel/AssetsDashboard/DashboardPOAssetTypesTable';
import DashboardPOStatusesTable from '@components/routes/AdminPanel/AssetsDashboard/DashboardPOStatusesTable';
import DashboardStatisticSummary from '@components/routes/AdminPanel/AssetsDashboard/DashboardStatisticSummary';

import {
	useMutateOrganizationDashboardAssetStatusesExport,
} from '@hooks/dashboardPO/useMutateOrganizationDashboardAssetStatusesExport';
import {
	useMutateOrganizationDashboardAssetTypesSummaryExport,
} from '@hooks/dashboardPO/useMutateOrganizationDashboardAssetTypesSummaryExport';
import useQueryOrganizationDashboardAssetsSummary from '@hooks/dashboardPO/useQueryOrganizationDashboardAssetsSummary';
import useQueryOrganizationDashboardAssetTypesSummary
	from '@hooks/dashboardPO/useQueryOrganizationDashboardAssetTypesSummary';
import useQueryOrganizationDashboardRentsSummary from '@hooks/dashboardPO/useQueryOrganizationDashboardRentsSummary';
import useQueryOrganizationDashboardRequestStatusesSummary
	from '@hooks/dashboardPO/useQueryOrganizationDashboardRequestStatusesSummary';

import { formatDate } from '@utils/date';
import {
	getOrganizationRequestsListData,
	getRangeTimestamp,
	getSummaryListData,
} from '@components/routes/AdminPanel/AssetsDashboard/utils';

import { ISortBy } from '@interfaces/components/table';
import { IExportFileDTO, IOrganizationExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import styles from './AssetsDashboard.module.scss';

interface IAssetsDashboard {
	id?: string;
}

const createSortHandler = (
	handleSortDirection: (value: React.SetStateAction<boolean | null>) => void,
	handleSortFieldName: (value: React.SetStateAction<string | null>) => void,
) => {
	return (sortBy: ISortBy[]) => {
		const { id: sortFieldName, desc: sortDirection } = sortBy[0] || { id: null, desc: null };

		handleSortDirection(sortDirection);
		handleSortFieldName(sortFieldName);
	};
};

const createExportHandler = (exportHandler: (params: IOrganizationExportFileDTO) => void, id?: string) => {
	return ({ fileType }: IExportFileDTO) => {
		if (!fileType || !id) return;

		exportHandler({ fileType, id });
	};
};

const AssetsDashboard = ({ id }: IAssetsDashboard) => {
	const {
		fromTimestamp,
		currentTimestamp,
	} = getRangeTimestamp();

	const [statusSortDirection, setStatusSortDirection] = useState<boolean | null>(null);
	const [statusSortFieldName, setStatusSortFieldName] = useState<string | null>(null);
	const [assetTypesSortDirection, setAssetTypesSortDirection] = useState<boolean | null>(null);
	const [assetTypesSortFieldName, setAssetTypesSortFieldName] = useState<string | null>(null);

	const handleStatusSort = useMemo(
		() => createSortHandler(setStatusSortDirection, setStatusSortFieldName)
		, []);

	const handleAssetTypesSort = useMemo(
		() => createSortHandler(setAssetTypesSortDirection, setAssetTypesSortFieldName)
		, []);

	const { exportOrganizationDashboardAssetTypesSummary } = useMutateOrganizationDashboardAssetTypesSummaryExport();
	const { exportOrganizationDashboardStatusesSummary } = useMutateOrganizationDashboardAssetStatusesExport();

	const handleStatusExport = useMemo(
		() => createExportHandler(exportOrganizationDashboardStatusesSummary, id)
		, [exportOrganizationDashboardStatusesSummary, id]);

	const handleAssetTypesExport = useMemo(
		() => createExportHandler(exportOrganizationDashboardAssetTypesSummary, id)
		, [exportOrganizationDashboardAssetTypesSummary, id]);

	const {
		watch,
		setValue,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues: {
			from: fromTimestamp,
			to: currentTimestamp,
		},
	});

	const fromDate = watch('from');
	const toDate = watch('to');

	const setFromDate = useCallback((date: number | null) => setValue('from', date), [setValue]);

	const setToDate = useCallback((date: number | null) => setValue('to', date), [setValue]);

	const fromText = useMemo(() => fromDate ? formatDate(fromDate) : '', [fromDate]);

	const toText = useMemo(() => toDate ? formatDate(toDate) : '', [toDate]);

	const {
		organizationDashboardAssetsSummary,
		isLoadingOrganizationDashboardAssetsSummary,
	} = useQueryOrganizationDashboardAssetsSummary({ id });

	const {
		organizationDashboardAssetTypesSummary,
		isLoadingOrganizationDashboardAssetTypesSummary,
	} = useQueryOrganizationDashboardAssetTypesSummary({
		id,
		sortDirection: assetTypesSortDirection,
		sortFieldName: assetTypesSortFieldName,
	});

	const {
		organizationDashboardRentsSummary,
		isLoadingOrganizationDashboardRentsSummary,
	} = useQueryOrganizationDashboardRentsSummary({
		id,
		fromText,
		toText,
	});

	const {
		organizationDashboardRequestStatusesSummary,
		isLoadingOrganizationDashboardRequestStatusesSummary,
	} = useQueryOrganizationDashboardRequestStatusesSummary({
		id,
		sortDirection: statusSortDirection,
		sortFieldName: statusSortFieldName,
	});

	const allAssetsData = getSummaryListData(organizationDashboardAssetsSummary);
	const requestsData = getOrganizationRequestsListData(organizationDashboardRentsSummary);

	return (
		<div className={styles.wrapper}>
			<DashboardStatisticSummary
				allAssetsData={allAssetsData}
				fromDate={fromDate}
				isLoadingDashboardAssetsSummary={isLoadingOrganizationDashboardAssetsSummary}
				isLoadingDashboardRentGeneralSummary={isLoadingOrganizationDashboardRentsSummary}
				requestsData={requestsData}
				setFromDate={setFromDate}
				setToDate={setToDate}
				statisticClassName={styles.statisticSummary}
				toDate={toDate}
			/>
			<div className={styles.tablesWrapper}>
				<DashboardPOStatusesTable
					dashboardRequestStatusesSummary={organizationDashboardRequestStatusesSummary}
					handleExport={handleStatusExport}
					handleSort={handleStatusSort}
					isLoadingDashboardRequestStatusesSummary={isLoadingOrganizationDashboardRequestStatusesSummary}
					isSingleOrganization
					sortDirection={statusSortDirection}
					sortFieldName={statusSortFieldName}
				/>
				<DashboardPOAssetTypesTable
					dashboardDashboardAssetTypesSummary={organizationDashboardAssetTypesSummary}
					handleExport={handleAssetTypesExport}
					handleSort={handleAssetTypesSort}
					isLoadingDashboardAssetTypesSummary={isLoadingOrganizationDashboardAssetTypesSummary}
					isSingleOrganization
					sortDirection={assetTypesSortDirection}
					sortFieldName={assetTypesSortFieldName}
				/>
			</div>
		</div>
	);
};

export default AssetsDashboard;
